import { ref } from 'vue'
import { defineStore } from 'pinia'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db, firebaseEnabled } from '@/firebase/client'
import type { AdminPermission, AdminProfile, AdminRole } from '@/types/models'
import { hasPermission } from '@/admin/permissions'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isAdmin = ref(false)
  const profile = ref<AdminProfile | null>(null)
  const role = ref<AdminRole | null>(null)
  const ready = ref(!firebaseEnabled)

  const resolveAdmin = async (candidate: User | null) => {
    user.value = candidate
    isAdmin.value = false
    profile.value = null
    role.value = null
    if (candidate && db) {
      const snapshot = await getDoc(doc(db, 'adminUsers', candidate.uid))
      if (snapshot.exists()) {
        const adminProfile = { id: snapshot.id, ...snapshot.data() } as AdminProfile
        if (adminProfile.active === true && ['admin', 'editor', 'reception'].includes(adminProfile.role)) {
          profile.value = adminProfile
          role.value = adminProfile.role
          isAdmin.value = true
        }
      }
    }
    ready.value = true
  }

  if (auth) onAuthStateChanged(auth, resolveAdmin)

  async function login(email: string, password: string) {
    if (!auth) throw new Error('Configure Firebase para iniciar sesión.')
    const credential = await signInWithEmailAndPassword(auth, email, password)
    await resolveAdmin(credential.user)
    if (!isAdmin.value) {
      await signOut(auth)
      throw new Error('El usuario no tiene rol administrativo.')
    }
  }

  async function logout() {
    if (auth) await signOut(auth)
    await resolveAdmin(null)
  }

  function can(permission?: AdminPermission) {
    return hasPermission(role.value, permission)
  }

  return { user, profile, role, isAdmin, ready, can, login, logout }
})
